import React from "react";
import { useField } from "formik";
import { Picker } from "@design-system/Picker/Picker.lib";
import { TransferRuleDirection } from "@common/types/transactionRule";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { TagsInput } from "@design-system/TagsInput/TagsInput";
import {
    TransactionRuleConditionFormSchema,
    DescriptionConditionType,
} from "../constants";
import styles from "../RuleForm.module.scss";
import { getFieldNamePrefix } from "../../utils";

interface Props {
    index: number;
    transferDirection?: TransferRuleDirection;
    controlButtonSize?: TriggerButtonProps["size"];
}

const conditionTypesOptions = Object.values(DescriptionConditionType).map(
    (v) => ({ value: v, label: v }),
);

const DESCRIPTION_SEPARATOR_KEYS = ["Enter"];

/**
 * Bank description condition: a match type plus a list of descriptions.
 * Only Enter commits, so spaces and commas stay inside one description.
 */
export const BankDescriptionConditionForm: React.FC<Props> = ({
    index,
    transferDirection,
    controlButtonSize,
}) => {
    const fieldNamePrefix = getFieldNamePrefix(index, transferDirection);
    const [conditionTypeField, , conditionTypeHelpers] = useField<
        TransactionRuleConditionFormSchema["descriptionConditionType"]
    >(`${fieldNamePrefix}.descriptionConditionType`);
    const [valueField, , valueHelpers] = useField<
        TransactionRuleConditionFormSchema["descriptionValue"]
    >(`${fieldNamePrefix}.descriptionValue`);

    return (
        <>
            <Picker.Select
                items={conditionTypesOptions}
                value={conditionTypeField.value ?? null}
                onValueChange={(value) => {
                    if (value !== null) {
                        conditionTypeHelpers.setValue(value);
                    }
                }}
                placeholder="Select type"
                trigger={
                    <Picker.Button
                        variant="primary"
                        size={controlButtonSize ?? "md"}
                    />
                }
            />
            <TagsInput
                className={styles.descriptionTagsInput}
                size="lg"
                separatorKeys={DESCRIPTION_SEPARATOR_KEYS}
                value={valueField.value ?? []}
                onValueChange={(values) => valueHelpers.setValue(values)}
                onBlur={() => valueHelpers.setTouched(true)}
                aria-label="Bank description"
            />
        </>
    );
};
